/**
 * i18next setup — docs/08-frontend-and-ux.md §11.
 *
 * ICU MessageFormat is the plugin of record because kinship labels need gendered
 * `select` and `selectordinal` forms (§11.4); it is wired now so the vocabulary
 * has somewhere to land in M3/M6. English is the only locale in M0.
 */

import i18n from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';
import { en } from './en.js';

export const SUPPORTED_LOCALES = ['en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const RTL_LOCALES = new Set<string>(['ar', 'he', 'fa', 'ur']);

const STORAGE_KEY = 'famtree.locale';

export function storedLocale(): Locale {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value && (SUPPORTED_LOCALES as readonly string[]).includes(value)) return value as Locale;
  } catch {
    // Private mode or blocked storage: fall through to the default.
  }
  return 'en';
}

export function applyDirection(locale: string): void {
  document.documentElement.lang = locale;
  document.documentElement.dir = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
}

export async function setupI18n(): Promise<typeof i18n> {
  const locale = storedLocale();
  await i18n
    .use(ICU)
    .use(initReactI18next)
    .init({
      resources: { en },
      lng: locale,
      fallbackLng: 'en',
      interpolation: { escapeValue: false }, // React escapes for us
      returnNull: false,
    });
  applyDirection(locale);
  return i18n;
}

export default i18n;
